import { useState, useEffect } from "react";
import { api, QUALIFICATION_LEVELS } from "../lib/utils";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import MaintenancePanel, { useMaintenanceSelection } from "../components/MaintenancePanel";
import { BookOpen, Plus, Pencil, Trash2, GraduationCap, Layers, FolderOpen, ChevronDown, ChevronRight } from "lucide-react";
import FrameworkLevelsDialog from "../components/FrameworkLevelsDialog";
import QualificationLevelsPanel from "../components/QualificationLevelsPanel";

// ======= Framework Dialog Component =======
function FrameworkDialog({ open, onOpenChange, editingFramework, onSaved, qualificationLevels }) {
  const [form, setForm] = useState({ name: "", description: "", qualification_level: "" });

  // Build the picker options from the live catalog (Qualification Levels
  // master table) so any custom level the admin just created is selectable
  // here without a code change. If the catalog hasn't loaded yet we fall
  // back to the static legacy list so the dialog is never empty.
  const levelOptions = (qualificationLevels && qualificationLevels.length > 0)
    ? qualificationLevels.map((l) => ({ value: l.code, label: l.name }))
    : QUALIFICATION_LEVELS;

  useEffect(() => {
    if (editingFramework) {
      setForm({
        name: editingFramework.name || "",
        description: editingFramework.description || "",
        qualification_level: editingFramework.qualification_level || "",
      });
    } else {
      setForm({ name: "", description: "", qualification_level: "" });
    }
  }, [editingFramework, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.qualification_level) delete payload.qualification_level;
      if (editingFramework) {
        await api.put(`/frameworks/${editingFramework.framework_id}`, payload);
        toast.success("Framework updated");
      } else {
        await api.post("/frameworks", payload);
        toast.success("Framework created");
      }
      onOpenChange(false);
      onSaved();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to save framework";
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="framework-dialog">
        <DialogHeader>
          <DialogTitle>{editingFramework ? "Edit Framework" : "Create Competency Framework"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label>Framework Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., RPL Business Management Framework"
              required
              data-testid="framework-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe the purpose and scope of this framework..."
              data-testid="framework-description-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Qualification Level (optional)</Label>
            <Select
              value={form.qualification_level || "none"}
              onValueChange={(v) => setForm({ ...form, qualification_level: v === "none" ? "" : v })}
            >
              <SelectTrigger data-testid="framework-level-select">
                <SelectValue placeholder="Any level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Any Level</SelectItem>
                {levelOptions.map((level) => (
                  <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-framework-btn">
              {editingFramework ? "Update" : "Create"} Framework
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ======= Main Component =======
export default function Competencies() {
  const [competencies, setCompetencies] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState("bachelor");
  const [fwDialogOpen, setFwDialogOpen] = useState(false);
  const [editingFramework, setEditingFramework] = useState(null);
  const [collapsedFrameworks, setCollapsedFrameworks] = useState({});
  // Live catalog from /api/qualification-levels (master maintenance table).
  // Used by Framework + Competency dialogs and the per-level Tabs so any
  // custom level added under Qualification Levels appears immediately
  // throughout this page.
  const [qualificationLevels, setQualificationLevels] = useState([]);
  const { selectedItems, selectMode, setSelectMode, handleSelectItem, isSelected, clearSelection, setSelectedItems } = useMaintenanceSelection();
  const [formData, setFormData] = useState({
    qualification_level: "bachelor",
    framework_name: "",
    unit_code: "",
    title: "",
    description: "",
    evidence_required: "",
    credit_hours: 3,
    display_order: 1,
    framework_id: "",
    is_core: false,
  });

  useEffect(() => {
    fetchCompetencies();
    fetchFrameworks();
    fetchQualificationLevels();
  }, []);

  const fetchQualificationLevels = async () => {
    try {
      const response = await api.get("/qualification-levels");
      setQualificationLevels(response.data || []);
    } catch (error) {
      console.error("Failed to load qualification levels:", error);
    }
  };

  const fetchCompetencies = async () => {
    try {
      const response = await api.get("/competencies");
      setCompetencies(response.data);
    } catch (error) {
      toast.error("Failed to load competencies");
    } finally {
      setLoading(false);
    }
  };

  const fetchFrameworks = async () => {
    try {
      const response = await api.get("/frameworks");
      setFrameworks(response.data);
    } catch (error) {
      console.error("Failed to load frameworks:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.framework_id) delete payload.framework_id;
      if (editingId) {
        await api.put(`/competencies/${editingId}`, payload);
        toast.success("Competency updated");
      } else {
        await api.post("/competencies", payload);
        toast.success("Competency created");
      }
      setDialogOpen(false);
      resetForm();
      fetchCompetencies();
    } catch (error) {
      console.error("Competency save error:", error);
      if (error.response?.status === 409) {
        toast.error(error.response.data.detail);
      } else if (error.response?.status === 401) {
        toast.error("Session expired. Please refresh the page.");
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Operation failed. Please check your connection and try again.");
      }
    }
  };

  const handleEdit = (comp) => {
    setEditingId(comp.competency_id);
    setFormData({
      qualification_level: comp.qualification_level,
      framework_name: comp.framework_name,
      unit_code: comp.unit_code,
      title: comp.title,
      description: comp.description,
      evidence_required: comp.evidence_required,
      credit_hours: comp.credit_hours,
      display_order: comp.display_order,
      framework_id: comp.framework_id || "",
      is_core: !!comp.is_core,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (competencyId) => {
    try {
      await api.delete(`/competencies/${competencyId}`);
      toast.success("Competency deleted");
      fetchCompetencies();
    } catch (error) {
      toast.error("Failed to delete competency");
    }
  };

  const handleDeleteFramework = async (frameworkId) => {
    try {
      await api.delete(`/frameworks/${frameworkId}`);
      toast.success("Framework deleted");
      fetchFrameworks();
      fetchCompetencies();
    } catch (error) {
      toast.error("Failed to delete framework");
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      qualification_level: selectedLevel,
      framework_name: "",
      unit_code: "",
      title: "",
      description: "",
      evidence_required: "",
      credit_hours: 3,
      display_order: 1,
      framework_id: "",
      is_core: false,
    });
  };

  const toggleFrameworkCollapse = (fwId) => {
    setCollapsedFrameworks(prev => ({ ...prev, [fwId]: !prev[fwId] }));
  };

  const filteredCompetencies = competencies.filter(c => c.qualification_level === selectedLevel);

  // Group competencies by framework_id
  const groupedCompetencies = () => {
    const groups = {};
    const ungrouped = [];

    filteredCompetencies.forEach(comp => {
      if (comp.framework_id) {
        if (!groups[comp.framework_id]) groups[comp.framework_id] = [];
        groups[comp.framework_id].push(comp);
      } else {
        ungrouped.push(comp);
      }
    });

    return { groups, ungrouped };
  };

  const getFrameworkName = (fwId) => {
    const fw = frameworks.find(f => f.framework_id === fwId);
    return fw ? fw.name : "Unknown Framework";
  };

  const renderCompetencyCard = (comp) => (
    <Card
      key={comp.competency_id}
      className="border-slate-200"
      data-testid={`competency-card-${comp.competency_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge variant="outline">{comp.unit_code}</Badge>
                <Badge>{comp.credit_hours} credits</Badge>
                {comp.is_core ? (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800 border border-amber-300" title="Mandatory for the gold Framework Achievement badge">
                    ★ Core
                  </Badge>
                ) : null}
                {comp.framework_id && (
                  <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 text-xs">
                    <Layers className="w-3 h-3 mr-1" />
                    {getFrameworkName(comp.framework_id)}
                  </Badge>
                )}
              </div>
              <h3 className="font-medium text-slate-900">{comp.title}</h3>
              <p className="text-sm text-slate-500 mt-1">{comp.description}</p>
              <p className="text-sm text-slate-500 mt-2">
                <span className="font-medium">Evidence Required:</span> {comp.evidence_required}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => handleEdit(comp)} data-testid={`edit-competency-${comp.competency_id}`}>
              <Pencil className="w-4 h-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Competency?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will remove this competency from the framework.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => handleDelete(comp.competency_id)}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const { groups, ungrouped } = groupedCompetencies();

  // Get frameworks relevant to the selected level
  const levelFrameworks = frameworks.filter(
    fw => fw.status === "active" && (!fw.qualification_level || fw.qualification_level === selectedLevel)
  );

  // Build picker/tab options from the live Qualification Levels catalog so
  // custom levels (Certificate II, NOSS L3, etc.) created in the
  // maintenance tab appear instantly across this page. Falls back to the
  // legacy hardcoded list if the catalog hasn't loaded yet.
  const levelOptions = (qualificationLevels && qualificationLevels.length > 0)
    ? qualificationLevels.map((l) => ({ value: l.code, label: l.name }))
    : QUALIFICATION_LEVELS;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="competencies-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Competency Frameworks</h1>
            <p className="text-slate-500 mt-1">Maintain qualification levels, frameworks and competencies in one place</p>
          </div>
        </div>

        <Tabs defaultValue="frameworks" className="w-full" data-testid="competencies-outer-tabs">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="frameworks" data-testid="tab-frameworks">
              <Layers className="w-4 h-4 mr-2" />
              Frameworks & Competencies
            </TabsTrigger>
            <TabsTrigger value="levels" data-testid="tab-qualification-levels">
              <GraduationCap className="w-4 h-4 mr-2" />
              Qualification Levels
            </TabsTrigger>
          </TabsList>

          <TabsContent value="levels" className="mt-6">
            <QualificationLevelsPanel onChanged={fetchQualificationLevels} />
          </TabsContent>

          <TabsContent value="frameworks" className="mt-6 space-y-6">
        {/* Action buttons row for Frameworks tab */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { setEditingFramework(null); setFwDialogOpen(true); }}
              data-testid="add-framework-btn"
            >
              <Layers className="w-4 h-4 mr-2" />
              New Framework
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-competency-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Competency
                </Button>
              </DialogTrigger>
              <MaintenancePanel
                entityType="competencies"
                entityLabel="Competencies"
                items={competencies}
                idField="competency_id"
                onRefresh={() => {
                  fetchCompetencies();
                  clearSelection();
                }}
                getItemLabel={(item) => `${item.unit_code} - ${item.title}`}
              />
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="competency-dialog">
                <DialogHeader>
                  <DialogTitle>{editingId ? "Edit Competency" : "Create Competency"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Qualification Level *</Label>
                      <Select
                        value={formData.qualification_level}
                        onValueChange={(v) => setFormData({ ...formData, qualification_level: v })}
                      >
                        <SelectTrigger data-testid="competency-level-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {levelOptions.map((level) => (
                            <SelectItem key={level.value} value={level.value}>
                              {level.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Framework Name *</Label>
                      <Input
                        value={formData.framework_name}
                        onChange={(e) => setFormData({ ...formData, framework_name: e.target.value })}
                        placeholder="e.g., RPL Business Management"
                        required
                        data-testid="competency-framework-input"
                      />
                    </div>
                  </div>
                  {/* Link to parent framework */}
                  <div className="space-y-2">
                    <Label>Parent Framework (optional)</Label>
                    <Select
                      value={formData.framework_id || "none"}
                      onValueChange={(v) => setFormData({ ...formData, framework_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger data-testid="competency-parent-framework-select">
                        <SelectValue placeholder="Select a framework..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Framework (Ungrouped)</SelectItem>
                        {frameworks.filter(fw => fw.status === "active").map((fw) => (
                          <SelectItem key={fw.framework_id} value={fw.framework_id}>
                            {fw.name} {fw.qualification_level ? `(${fw.qualification_level})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Unit Code *</Label>
                      <Input
                        value={formData.unit_code}
                        onChange={(e) => setFormData({ ...formData, unit_code: e.target.value })}
                        placeholder="e.g., RPL/BSC/4001"
                        required
                        data-testid="competency-code-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Credit Hours *</Label>
                      <Input
                        type="number"
                        value={formData.credit_hours}
                        onChange={(e) => setFormData({ ...formData, credit_hours: parseInt(e.target.value) || 3 })}
                        min={1}
                        max={20}
                        required
                        data-testid="competency-credits-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Competency Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g., Strategic Leadership and Management"
                      required
                      data-testid="competency-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Detailed description of the competency..."
                      data-testid="competency-description-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Evidence Required *</Label>
                    <Textarea
                      value={formData.evidence_required}
                      onChange={(e) => setFormData({ ...formData, evidence_required: e.target.value })}
                      placeholder="What evidence is needed to demonstrate this competency?"
                      required
                      data-testid="competency-evidence-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Display Order</Label>
                    <Input
                      type="number"
                      value={formData.display_order}
                      onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 1 })}
                      min={1}
                      data-testid="competency-order-input"
                    />
                  </div>
                  <div className="flex items-start justify-between gap-4 pt-2 border-t border-slate-100">
                    <div className="flex-1">
                      <Label htmlFor="comp-is-core" className="font-medium">Mark as <span className="text-amber-700">Core</span> competency</Label>
                      <p className="text-xs text-slate-500 mt-1">
                        When ON, this competency becomes <em>mandatory</em> for the gold Framework Achievement badge.
                        A candidate must score above threshold here, even if the overall coverage % is met.
                      </p>
                    </div>
                    <input
                      id="comp-is-core"
                      type="checkbox"
                      checked={!!formData.is_core}
                      onChange={(e) => setFormData({ ...formData, is_core: e.target.checked })}
                      className="w-5 h-5 mt-1 accent-amber-600"
                      data-testid="competency-is-core-toggle"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="save-competency-btn">
                      {editingId ? "Update" : "Create"} Competency
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Framework Cards */}
        {frameworks.filter(fw => fw.status === "active").length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {frameworks.filter(fw => fw.status === "active").map(fw => {
              const compCount = competencies.filter(c => c.framework_id === fw.framework_id).length;
              return (
                <Card key={fw.framework_id} className="border-indigo-100 bg-gradient-to-br from-indigo-50/50 to-white" data-testid={`framework-card-${fw.framework_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <Layers className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900 text-sm">{fw.name}</h4>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-500">{compCount} competencies</span>
                            {fw.qualification_level && (
                              <Badge variant="outline" className="text-xs py-0">{fw.qualification_level}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <FrameworkLevelsDialog framework={fw} onSaved={fetchFrameworks} />
                        <Button
                          variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => { setEditingFramework(fw); setFwDialogOpen(true); }}
                          data-testid={`edit-framework-${fw.framework_id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Framework?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will delete the framework. Competencies under it will become ungrouped.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteFramework(fw.framework_id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    {fw.description && (
                      <p className="text-xs text-slate-500 mt-2 line-clamp-2">{fw.description}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Framework Dialog */}
        <FrameworkDialog
          open={fwDialogOpen}
          onOpenChange={setFwDialogOpen}
          editingFramework={editingFramework}
          onSaved={() => { fetchFrameworks(); fetchCompetencies(); }}
          qualificationLevels={qualificationLevels}
        />

        {/* Competency List with Level Tabs */}
        <Tabs value={selectedLevel} onValueChange={setSelectedLevel}>
          <TabsList>
            {levelOptions.map((level) => (
              <TabsTrigger key={level.value} value={level.value} data-testid={`tab-${level.value}`}>
                {level.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {levelOptions.map((level) => (
            <TabsContent key={level.value} value={level.value}>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse"></div>
                  ))}
                </div>
              ) : filteredCompetencies.length > 0 ? (
                <div className="space-y-6">
                  {/* Grouped under frameworks */}
                  {Object.entries(groups).map(([fwId, comps]) => {
                    const fw = frameworks.find(f => f.framework_id === fwId);
                    const isCollapsed = collapsedFrameworks[fwId];
                    return (
                      <div key={fwId} className="border border-indigo-100 rounded-lg overflow-hidden" data-testid={`framework-group-${fwId}`}>
                        <button
                          className="w-full flex items-center justify-between px-4 py-3 bg-indigo-50/60 hover:bg-indigo-50 transition-colors text-left"
                          onClick={() => toggleFrameworkCollapse(fwId)}
                          data-testid={`toggle-framework-${fwId}`}
                        >
                          <div className="flex items-center gap-2">
                            {isCollapsed ? <ChevronRight className="w-4 h-4 text-indigo-500" /> : <ChevronDown className="w-4 h-4 text-indigo-500" />}
                            <Layers className="w-4 h-4 text-indigo-600" />
                            <span className="font-medium text-slate-800">{fw?.name || "Unknown Framework"}</span>
                            <Badge variant="secondary" className="text-xs">{comps.length} competencies</Badge>
                          </div>
                        </button>
                        {!isCollapsed && (
                          <div className="p-3 space-y-3">
                            {comps.map(renderCompetencyCard)}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Ungrouped competencies */}
                  {ungrouped.length > 0 && (
                    <div>
                      {Object.keys(groups).length > 0 && (
                        <div className="flex items-center gap-2 mb-3 px-1">
                          <FolderOpen className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-500">Ungrouped Competencies</span>
                          <Badge variant="outline" className="text-xs">{ungrouped.length}</Badge>
                        </div>
                      )}
                      <div className="space-y-3">
                        {ungrouped.map(renderCompetencyCard)}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Card className="border-slate-200">
                  <CardContent className="p-12 text-center">
                    <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">
                      No Competencies for {level.label}
                    </h3>
                    <p className="text-slate-500 mb-4">
                      Add competencies to define the assessment framework
                    </p>
                    <Button onClick={() => { setFormData({...formData, qualification_level: level.value}); setDialogOpen(true); }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Competency
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          ))}
        </Tabs>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
